import {
	BuildAutomationStep,
	BuildStatus,
	isAnalyzableBuildStatus,
	isAutomationStepEnabled,
} from '@cadstrom/cadstrom-shared';

import type { BuildAutomationMode, RevisionStatus } from '@cadstrom/cadstrom-shared';

export interface AnalyzeTriggerContext {
	revisionStatus: RevisionStatus;
	/** Absent means manual: the customer drives the analysis gate. */
	revisionAutomationMode?: BuildAutomationMode;
	/** The organization is on the billing flow that owns the Analyze gate. */
	showBillingFlow: boolean;
	/** Optimistic flag set the moment the customer clicked Analyze, until the status catches up. */
	isAnalyzing: boolean;
}

/**
 * Whether the system, not the customer, presses Analyze for this revision.
 *
 * @param mode - The revision's automation mode, if any.
 * @returns True only for a revision enrolled in automatic mode.
 */
export function isAnalysisAutomated(mode: BuildAutomationMode | undefined): boolean {
	return isAutomationStepEnabled(mode, BuildAutomationStep.DispatchAnalysis);
}

/**
 * Whether analysis should present as in progress: the durable `processing_design`
 * status, the optimistic window after the customer's click, or an automated revision
 * waiting at the gate the automation is about to take it through.
 *
 * @param ctx - The revision's status, mode, and page state.
 * @returns True when the page should show analysis as running.
 */
export function isAnalysisInProgress(ctx: AnalyzeTriggerContext): boolean {
	if (!ctx.showBillingFlow) {
		return false;
	}
	if (ctx.revisionStatus === BuildStatus.ProcessingDesign || ctx.isAnalyzing) {
		return true;
	}
	return isAnalysisAutomated(ctx.revisionAutomationMode) && ctx.revisionStatus === BuildStatus.ReadyToAnalyze;
}

/**
 * Whether the customer may be offered the Analyze trigger: in a status the server will
 * actually accept an analysis from, with no run in flight — and never for a revision the
 * automation drives itself, and never for a finished or delivered analysis.
 *
 * @param ctx - The revision's status, mode, and page state.
 * @returns True when the Analyze button should render.
 */
export function canCustomerTriggerAnalysis(ctx: AnalyzeTriggerContext): boolean {
	if (isAnalysisAutomated(ctx.revisionAutomationMode)) {
		return false;
	}
	// Mirrors the server's precondition rather than approximating it: offering the button
	// mid-merge produced a click the API refused.
	if (ctx.revisionStatus === undefined || !isAnalyzableBuildStatus(ctx.revisionStatus)) {
		return false;
	}
	return (
		ctx.showBillingFlow &&
		!isAnalysisInProgress(ctx) &&
		ctx.revisionStatus !== BuildStatus.AnalysisComplete &&
		ctx.revisionStatus !== BuildStatus.Complete
	);
}
